import * as stylex from '@stylexjs/stylex';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { formatTimeAgo } from '@/datetime/timeAgo.utilities';
import { headerStyles as styles } from '@/header/header.styles';
import { workstepCaretMarker } from '@/header/workstepCaret.stylex';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { reloadWorksheet } from '@/workbench/workbench.utilities';
import {
  useCurrentWorkstepCreatedAt,
  useCurrentWorkstepId,
  useIsWorkstepError,
  useLastWorkstep,
  useNextWorkstep,
  usePreviousWorkstep,
} from '@/worksteps/worksteps.store';
import { getWorksteps, setCurrentWorkstep } from '@/worksteps/worksteps.utilities';
import { DirectionEnum } from '@seeqdev/api/model/WorkstepChronologyOutputV1';
import {
  faCaretDown,
  faExclamationTriangle,
  faReply,
  faReplyAll,
  faShare,
} from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon, TriggerWithDropdown } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type { WorkstepChronologyEntryV1 } from '@seeqdev/api/model/WorkstepChronologyEntryV1';

export type WorkstepButtonType = 'previous' | 'next' | 'last';

interface WorkstepIconsProps {
  isViewMode: boolean;
  isEditMode: boolean;
}

export const WorkstepIcons = ({ isViewMode, isEditMode }: WorkstepIconsProps) => {
  const previous = usePreviousWorkstep();
  const next = useNextWorkstep();
  const last = useLastWorkstep();
  const currentId = useCurrentWorkstepId();
  const currentCreatedAt = useCurrentWorkstepCreatedAt();
  const isError = useIsWorkstepError();
  const previousAvailable = !_.isUndefined(previous);
  const nextAvailable = !_.isUndefined(next);
  const lastAvailable = !_.isUndefined(last) && last !== currentId;
  const { t } = useTranslation();

  const [beforeShown, setBeforeShown] = useState(false);
  const [afterShown, setAfterShown] = useState(false);
  const [chronology, setChronology] = useState<{ value: string; label: string }[]>();

  useEffect(() => {
    if (beforeShown || afterShown) {
      getWorksteps(
        sqWorkbenchStore.stateParams.workbookId,
        sqWorkbenchStore.stateParams.worksheetId,
        currentId ?? '',
        beforeShown ? DirectionEnum.Before : DirectionEnum.After,
      ).then((response) => {
        setChronology(mapChronology(response.entries, sqWorkbenchStore.userLanguage));
      });
    } else {
      setChronology(undefined);
    }
  }, [beforeShown, afterShown, currentId, currentCreatedAt]);

  const goToWorkstep = async (
    workstepId: string,
    action: 'Previous' | 'Next' | 'Last',
    fromList = false,
  ): Promise<void> => {
    doTrack(TRACK.WORKSTEPS, action, fromList ? 'from list' : 'button');
    await setCurrentWorkstep(
      sqWorkbenchStore.stateParams.workbookId,
      sqWorkbenchStore.stateParams.worksheetId,
      workstepId,
    );
    reloadWorksheet();
  };

  return (
    <div {...stylex.props(isViewMode ? styles.viewWorksteps : styles.worksteps)} data-testid="workstepIcons">
      {isEditMode &&
        !isViewMode &&
        !sqWorkbookStore.isTopic &&
        !sqWorkbookStore.isVantage &&
        !sqWorkbookStore.isActionBoard && (
          <div {...stylex.props(styles.historyActions)}>
            {isError && (
              <Icon
                icon={faExclamationTriangle}
                xstyle={[styles.navigationIcon, styles.workstepAction, styles.historyError]}
                type="danger"
                tooltip={t('WORKSTEPS.ERROR')}
                tooltipPlacement="bottom"
              />
            )}

            <Icon
              icon={faReply}
              xstyle={[
                styles.navigationIcon,
                styles.previousWorkstep,
                previousAvailable ? styles.workstepAction : styles.unavailableWorkstep,
              ]}
              type={previousAvailable ? 'theme-light' : 'gray'}
              onClick={() => {
                if (previousAvailable && previous) {
                  goToWorkstep(previous, 'Previous');
                }
              }}
              testId="previousWorkstep"
              tooltip={t('WORKSTEPS.PREVIOUS')}
              tooltipPlacement="bottom"
            />

            <TriggerWithDropdown
              disabled={!previousAvailable}
              onOpenChange={(show) => setBeforeShown(show || false)}
              xstyle={styles.previousHistoryMenu}
              contentXstyle={styles.historyMenu}
              containerTestId="previousWorkstepPopover"
              triggerIcon={
                <div {...stylex.props(workstepCaretMarker)}>
                  <Icon
                    testId="previousWorkstepPulldown"
                    icon={faCaretDown}
                    xstyle={[
                      styles.historyCaret,
                      previousAvailable ? styles.workstepAction : styles.unavailableWorkstep,
                      previousAvailable ? workstepMenuStyles.availableCaret : workstepMenuStyles.unavailableCaret,
                    ]}
                    type={previousAvailable ? 'theme-light' : 'gray'}
                  />
                </div>
              }
              dropdownItems={
                previousAvailable && beforeShown && chronology
                  ? chronology.map(({ value, label }) => ({
                      label,
                      itemXstyle: workstepMenuStyles.item,
                      labelXstyle: workstepMenuStyles.item,
                      onClick: () => {
                        setBeforeShown(false);
                        goToWorkstep(value, 'Previous', true);
                      },
                    }))
                  : []
              }
              tooltipPlacement="bottom"
            />

            <Icon
              icon={faShare}
              xstyle={[styles.navigationIcon, nextAvailable ? styles.workstepAction : styles.unavailableWorkstep]}
              type={nextAvailable ? 'theme-light' : 'gray'}
              onClick={() => (nextAvailable ? goToWorkstep(next!, 'Next') : null)}
              testId="nextWorkstep"
              tooltip={t('WORKSTEPS.NEXT')}
              tooltipPlacement="bottom"
            />

            <TriggerWithDropdown
              disabled={!nextAvailable}
              onOpenChange={(show) => setAfterShown(show || false)}
              xstyle={styles.nextHistoryMenu}
              contentXstyle={styles.historyMenu}
              triggerIcon={
                <div {...stylex.props(workstepCaretMarker)}>
                  <Icon
                    testId="nextWorkstepPulldown"
                    icon={faCaretDown}
                    xstyle={[
                      styles.historyCaret,
                      nextAvailable ? styles.workstepAction : styles.unavailableWorkstep,
                      nextAvailable ? workstepMenuStyles.availableCaret : workstepMenuStyles.unavailableCaret,
                    ]}
                    type={nextAvailable ? 'theme-light' : 'gray'}
                  />
                </div>
              }
              placement="bottom"
              containerTestId="nextWorkstepPopover"
              dropdownItems={
                nextAvailable && afterShown && chronology
                  ? chronology.map(({ value, label }) => ({
                      label,
                      itemXstyle: workstepMenuStyles.item,
                      labelXstyle: workstepMenuStyles.item,
                      onClick: () => {
                        setAfterShown(false);
                        goToWorkstep(value, 'Next', true);
                      },
                    }))
                  : []
              }
            />

            <Icon
              icon={faReplyAll}
              xstyle={[
                styles.navigationIcon,
                styles.mirroredWorkstep,
                styles.lastWorkstep,
                lastAvailable ? styles.workstepAction : styles.unavailableWorkstep,
              ]}
              type={lastAvailable ? 'theme-light' : 'gray'}
              onClick={() => (lastAvailable ? goToWorkstep(last!, 'Last') : null)}
              testId="lastWorkstep"
              tooltip={t('WORKSTEPS.LAST')}
              tooltipPlacement="bottom"
            />
          </div>
        )}
    </div>
  );
};

const mapChronology = (
  entries: WorkstepChronologyEntryV1[] | undefined,
  language: string,
  currentTime: number = Date.now(),
) => {
  // If there are no entries, return an empty array
  if (!entries) {
    return [];
  }

  // Deduplicate the entries to avoid showing too many options
  const minDeltaInMs = 5_000;
  const deduped: WorkstepChronologyEntryV1[] = [];
  let last: WorkstepChronologyEntryV1;
  entries.forEach((value, index) => {
    if (index === 0 || (value.createdAt || 0) <= (last.createdAt || 0) - minDeltaInMs) {
      deduped.push(value);
      last = value;
    }
  });

  // Create a relative time formatter
  const rtf = new Intl.RelativeTimeFormat(language, { numeric: 'always', style: 'narrow' });

  // Map the entries to the options
  const mapping = (entry: WorkstepChronologyEntryV1) => ({
    value: entry.workstepId!,
    label: formatTimeAgo(rtf, Math.floor((currentTime - (entry.createdAt || 0)) / 1000), entry.createdAt || 0),
  });
  return deduped.map(mapping);
};

export const mapChronologyExposedForTestingOnly = mapChronology;

export const workstepMenuStyles = stylex.create({
  item: { color: { 'default': colors.text, ':hover': colors.text } },
  availableCaret: {
    color: {
      default: colors.accentSubtle,
      [stylex.when.ancestor(':is(:hover, :focus)', workstepCaretMarker)]: colors.icon,
    },
  },
  unavailableCaret: {
    color: {
      default: colors.textMuted,
      [stylex.when.ancestor(':is(:hover, :focus)', workstepCaretMarker)]: colors.icon,
    },
  },
});
